/**
 * LevNutri — Módulo Financeiro
 * Abas: Resumo do Mês | Lançamentos | Histórico
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  LineChart, Line, PieChart, Pie, Cell, ResponsiveContainer,
} from 'recharts';
import { financialService, settingsService } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import ConfirmModal from '../Modal/ConfirmModal';
import { Ico, emo } from '../ui/Ico';

// ── Palette ───────────────────────────────────────────────────────────────────
const C = {
  primary: '#d4a574', dark: '#5a3e2b', darker: '#2f2a26',
  bg: '#fbf8f5', card: '#ffffff', border: '#f6f0ea',
  green: '#46714f', red: '#c46a60', blue: '#7189a6',
  yellow: '#c4933f', purple: '#7189a6',
};

const PIE_COLORS = [C.primary, C.blue, '#6f9a77', C.purple, C.yellow, '#c4933f'];
const PT_MONTHS  = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const MONTHS_PT  = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho',
                    'Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const CAT_RECEITA  = ['Consulta Presencial','Consulta Online','Outros'];
const CAT_DESPESA  = ['Aluguel','Material','Marketing','Cursos','Outros'];

const fmtBRL = v => new Intl.NumberFormat('pt-BR',{style:'currency',currency:'BRL'}).format(v||0);
const fmtPct = v => `${Math.round(v||0)}%`;

function toISO(d) {
  const dt=new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,'0')}-${String(dt.getDate()).padStart(2,'0')}`;
}

function fmtDate(s) {
  if (!s) return '';
  const [y,m,d] = s.split('-');
  return `${d}/${m}/${y}`;
}

function fmtMonth(ym) {
  const [y,m] = ym.split('-');
  return `${PT_MONTHS[parseInt(m,10)-1]}/${y.slice(2)}`;
}

// Export CSV das transações carregadas (separador ; — abre direto no Excel BR)
function exportarCSV(records) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const linhas = [
    ['Data', 'Descrição', 'Categoria', 'Tipo', 'Valor (R$)'].join(';'),
    ...[...records]
      .sort((a, b) => a.data.localeCompare(b.data))
      .map(r => [
        esc(r.data),
        esc(r.descricao),
        esc(r.categoria),
        esc(r.tipo === 'receita' ? 'Receita' : 'Despesa'),
        String(parseFloat(r.valor || 0).toFixed(2)).replace('.', ','),
      ].join(';')),
  ];
  // BOM para o Excel reconhecer UTF-8 (acentos)
  const blob = new Blob(['﻿' + linhas.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `levnutri-financeiro-${new Date().toISOString().slice(0, 7)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function groupByWeek(records) {
  const w = [{l:'Sem 1',rec:0,dep:0},{l:'Sem 2',rec:0,dep:0},{l:'Sem 3',rec:0,dep:0},{l:'Sem 4+',rec:0,dep:0}];
  (records||[]).forEach(r => {
    const day = parseInt((r.data||'').split('-')[2]||'1',10);
    const wi  = day<=7?0:day<=14?1:day<=21?2:3;
    if (r.tipo==='receita') w[wi].rec += parseFloat(r.valor||0);
    else w[wi].dep += parseFloat(r.valor||0);
  });
  return w;
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function Card({children,style}) {
  return <div style={{background:C.card,borderRadius:'12px',padding:'1.25rem',border:`1px solid ${C.border}`,boxShadow:'0 1px 4px rgba(0,0,0,.05)',...style}}>{children}</div>;
}

function SectionLabel({children}) {
  return <p style={{margin:'0 0 0.6rem',fontSize:'0.7rem',fontWeight:600,color:'#a39b93',textTransform:'uppercase',letterSpacing:'.1em'}}>{children}</p>;
}

function SummaryCard({icon,label,value,sub,color,progress}) {
  return (
    <Card style={{flex:1,minWidth:'160px'}}>
      <div style={{display:'flex',alignItems:'flex-start',gap:'0.75rem'}}>
        <div style={{width:38,height:38,borderRadius:'10px',background:color+'18',flexShrink:0,
                     display:'flex',alignItems:'center',justifyContent:'center',fontSize:'1.1rem'}}>{emo(icon)}</div>
        <div style={{flex:1,minWidth:0}}>
          <p style={{margin:'0 0 0.1rem',fontSize:'0.7rem',fontWeight:600,color:'#736b64',textTransform:'uppercase',letterSpacing:'.04em'}}>{emo(label)}</p>
          <p style={{margin:0,fontSize:'1.3rem',fontWeight:700,color:C.darker,lineHeight:1.15}}>{value}</p>
          {sub&&<p style={{margin:'0.1rem 0 0',fontSize:'0.72rem',color:'#a39b93'}}>{sub}</p>}
        </div>
      </div>
      {progress!==undefined&&(
        <div style={{marginTop:'0.7rem'}}>
          <div style={{height:5,background:'#f6f0ea',borderRadius:'999px',overflow:'hidden'}}>
            <div style={{height:'100%',borderRadius:'999px',width:`${Math.min(progress,100)}%`,
                         background:progress>=100?C.green:C.primary,transition:'width .4s ease'}}/>
          </div>
          <p style={{margin:'0.2rem 0 0',fontSize:'0.7rem',color:'#a39b93',textAlign:'right'}}>{Math.round(progress)}% da meta</p>
        </div>
      )}
    </Card>
  );
}

const inp = {padding:'0.5rem 0.7rem',border:`1px solid ${C.border}`,borderRadius:'8px',
             fontSize:'0.88rem',color:C.darker,background:C.bg,width:'100%',boxSizing:'border-box',outline:'none'};
const lbl = {fontSize:'0.72rem',fontWeight:600,color:C.dark,textTransform:'uppercase',letterSpacing:'.04em',display:'block',marginBottom:'0.25rem'};

// ── New Record Modal ──────────────────────────────────────────────────────────
function NewRecordModal({ tipo: initTipo, selMonth, onClose, onCreated }) {
  const { showSuccess, showError } = useToast();
  const [form, setForm] = useState({
    tipo: initTipo, categoria: '', descricao: '', valor: '',
    data: toISO(new Date()),
  });
  const [saving, setSaving] = useState(false);
  const set = (k,v) => setForm(f=>({...f,[k]:v}));
  const cats = form.tipo==='receita' ? CAT_RECEITA : CAT_DESPESA;

  const submit = async () => {
    if (!form.categoria||!form.descricao||!form.valor) { showError('Preencha todos os campos'); return; }
    setSaving(true);
    try {
      const res = await financialService.create({
        tipo: form.tipo, categoria: form.categoria,
        descricao: form.descricao, valor: parseFloat(form.valor),
        data: form.data,
      });
      if (res.success) { showSuccess('Lançamento registrado!'); onCreated(res.data); onClose(); }
      else showError(res.message||'Erro ao registrar');
    } catch { showError('Erro ao registrar lançamento'); }
    finally { setSaving(false); }
  };

  const overlay={position:'fixed',inset:0,background:'rgba(0,0,0,.5)',display:'flex',
                 alignItems:'center',justifyContent:'center',zIndex:9997,padding:'1rem'};
  const box={background:'#ffffff',borderRadius:'14px',padding:'1.5rem',width:'100%',
             maxWidth:'440px',maxHeight:'90vh',overflowY:'auto',boxShadow:'0 24px 64px rgba(0,0,0,.2)'};

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e=>e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.25rem'}}>
          <h2 style={{margin:0,fontSize:'1.05rem',fontWeight:700,color:C.darker}}>
            {emo(form.tipo==='receita'?'💰 Nova Receita':'📤 Nova Despesa')}
          </h2>
          <button onClick={onClose} style={{background:'none',border:'none',fontSize:'1.2rem',cursor:'pointer',color:'#a39b93',padding:0}}><Ico e="✕" /></button>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:'0.85rem'}}>
          {/* Tipo */}
          <div style={{display:'flex',gap:'0.5rem'}}>
            {['receita','despesa'].map(t=>(
              <button key={t} onClick={()=>{set('tipo',t);set('categoria','');}}
                style={{flex:1,padding:'0.5rem',borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'0.83rem',
                        border:`1.5px solid ${form.tipo===t?(t==='receita'?C.green:C.red):C.border}`,
                        background:form.tipo===t?(t==='receita'?'#edf4ee':'#f8ebe9'):'#ffffff',
                        color:form.tipo===t?(t==='receita'?'#2f5236':'#a8483f'):C.dark}}>
                {t==='receita'?'+ Receita':'- Despesa'}
              </button>
            ))}
          </div>
          <div><label style={lbl}>Categoria</label>
            <select style={inp} value={form.categoria} onChange={e=>set('categoria',e.target.value)}>
              <option value="">Selecione...</option>
              {cats.map(c=><option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Descrição</label>
            <input style={inp} value={form.descricao} onChange={e=>set('descricao',e.target.value)} placeholder="Ex: Aluguel sala de atendimento"/>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.75rem'}}>
            <div><label style={lbl}>Valor (R$)</label>
              <input style={inp} type="number" step="0.01" value={form.valor} onChange={e=>set('valor',e.target.value)} placeholder="0,00"/>
            </div>
            <div><label style={lbl}>Data</label>
              <input style={inp} type="date" value={form.data} onChange={e=>set('data',e.target.value)}/>
            </div>
          </div>
          <div style={{display:'flex',gap:'0.6rem',justifyContent:'flex-end',paddingTop:'0.4rem'}}>
            <button onClick={onClose} style={{padding:'0.55rem 1.1rem',background:'#ffffff',color:C.dark,
              border:`1px solid ${C.border}`,borderRadius:'8px',cursor:'pointer',fontWeight:600}}>Cancelar</button>
            <button onClick={submit} disabled={saving}
              style={{padding:'0.55rem 1.25rem',background:saving?'#ebe3da':(form.tipo==='receita'?C.green:C.red),
                color:'#ffffff',border:'none',borderRadius:'8px',cursor:saving?'not-allowed':'pointer',fontWeight:600}}>
              {saving?'Salvando...':'Registrar'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Custom tooltip ────────────────────────────────────────────────────────────
function FinTooltip({active,payload,label}) {
  if (!active||!payload?.length) return null;
  return (
    <div style={{background:'#ffffff',border:`1px solid ${C.border}`,borderRadius:'8px',padding:'0.6rem 0.85rem',
                 fontSize:'0.82rem',boxShadow:'0 4px 12px rgba(0,0,0,.1)'}}>
      <p style={{margin:'0 0 0.3rem',fontWeight:600,color:C.darker}}>{emo(label)}</p>
      {payload.map((p,i)=><p key={i} style={{margin:'0.1rem 0',color:p.color}}>{p.name}: {fmtBRL(p.value)}</p>)}
    </div>
  );
}

// ── ABA RESUMO ────────────────────────────────────────────────────────────────
function TabResumo({ monthData, settings, loading }) {
  if (loading) return <div style={{textAlign:'center',color:'#a39b93',padding:'3rem'}}>Carregando...</div>;
  if (!monthData) return <div style={{textAlign:'center',color:'#a39b93',padding:'3rem'}}>Sem dados para este mês</div>;

  const { receita=0, despesas=0, lucro=0, records=[], cat_breakdown={} } = monthData;
  const metaReceita = parseFloat(settings?.meta_receita_mes||3000);
  const recPct      = metaReceita>0?(receita/metaReceita)*100:0;

  const weeklyData = groupByWeek(records).map(w=>({name:w.l,Receita:w.rec,Despesas:w.dep}));
  const catRecData = Object.entries(cat_breakdown.receita||{}).map(([name,value])=>({name,value}));
  const sortedRecs = [...records].sort((a,b)=>b.data.localeCompare(a.data));

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'1.25rem'}}>
      {/* Summary cards */}
      <div style={{display:'flex',gap:'0.75rem',flexWrap:'wrap'}}>
        <SummaryCard icon="💰" label="Receita Total" value={fmtBRL(receita)} color={C.green}
          sub={`Meta: ${fmtBRL(metaReceita)}`} progress={recPct}/>
        <SummaryCard icon="📤" label="Despesas" value={fmtBRL(despesas)} color={C.red}
          sub={`Aluguel: ${fmtBRL(settings?.valor_aluguel_espaco||0)}`}/>
        <SummaryCard icon="💵" label="Lucro Líquido" value={fmtBRL(lucro)} color={lucro>=0?C.green:C.red}/>
        <SummaryCard icon="🎯" label="Meta Atingida" value={fmtPct(recPct)} color={C.primary}
          progress={recPct}/>
      </div>

      {/* Charts row */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))',gap:'1rem'}}>
        <Card>
          <p style={{margin:'0 0 1rem',fontWeight:600,color:C.darker,fontSize:'0.88rem'}}><Ico e="📊" sp />Receita vs Despesas por Semana</p>
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={weeklyData} margin={{top:4,right:4,left:-10,bottom:0}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f6f0ea"/>
              <XAxis dataKey="name" tick={{fontSize:11,fill:'#a39b93'}}/>
              <YAxis tick={{fontSize:11,fill:'#a39b93'}} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
              <Tooltip content={<FinTooltip/>}/>
              <Legend iconSize={9} wrapperStyle={{fontSize:'0.75rem'}}/>
              <Bar dataKey="Receita" fill={C.green} radius={[3,3,0,0]}/>
              <Bar dataKey="Despesas" fill={C.red} radius={[3,3,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card>
          <p style={{margin:'0 0 0.5rem',fontWeight:600,color:C.darker,fontSize:'0.88rem'}}><Ico e="🥧" sp />Receitas por Categoria</p>
          {catRecData.length===0
            ? <div style={{height:190,display:'flex',alignItems:'center',justifyContent:'center'}}>
                <p style={{color:'#d9d0c6',fontSize:'0.82rem'}}>Sem receitas registradas</p>
              </div>
            : <ResponsiveContainer width="100%" height={190}>
                <PieChart>
                  <Pie data={catRecData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={28}>
                    {catRecData.map((_,i)=><Cell key={i} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                  </Pie>
                  <Tooltip formatter={(v,n)=>[fmtBRL(v),n]}/>
                  <Legend iconSize={9} wrapperStyle={{fontSize:'0.74rem'}} iconType="circle"/>
                </PieChart>
              </ResponsiveContainer>
          }
        </Card>
      </div>

      {/* Transaction table */}
      <Card>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'1rem',flexWrap:'wrap',gap:'0.5rem'}}>
          <p style={{margin:0,fontWeight:600,color:C.darker,fontSize:'0.88rem'}}>
            <Ico e="📋" sp />Transações do Mês <span style={{color:'#a39b93',fontWeight:400,fontSize:'0.8rem'}}>({records.length} registros)</span>
          </p>
          {records.length > 0 && (
            <button
              onClick={() => exportarCSV(records)}
              style={{padding:'0.4rem 0.9rem',background:'#ffffff',color:C.dark,border:`1px solid ${C.border}`,
                      borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'0.78rem'}}
            >
              <Ico e="⬇" sp />Exportar CSV
            </button>
          )}
        </div>
        {records.length===0
          ? <p style={{textAlign:'center',color:'#d9d0c6',padding:'1rem 0'}}>Nenhuma transação</p>
          : <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.85rem'}}>
                <thead>
                  <tr style={{borderBottom:`2px solid ${C.border}`}}>
                    {['Data','Descrição','Categoria','Valor','Tipo'].map(h=>(
                      <th key={h} style={{textAlign:'left',padding:'0.4rem 0.6rem',fontSize:'0.72rem',
                                          color:'#a39b93',textTransform:'uppercase',fontWeight:600}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRecs.map(r=>(
                    <tr key={r.id} style={{borderBottom:`1px solid ${C.border}`}}>
                      <td style={{padding:'0.5rem 0.6rem',color:'#736b64',whiteSpace:'nowrap'}}>{fmtDate(r.data)}</td>
                      <td style={{padding:'0.5rem 0.6rem',color:C.darker,maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{emo(r.descricao)}</td>
                      <td style={{padding:'0.5rem 0.6rem',color:'#736b64'}}>{r.categoria}</td>
                      <td style={{padding:'0.5rem 0.6rem',fontWeight:600,
                                   color:r.tipo==='receita'?C.green:C.red,whiteSpace:'nowrap'}}>
                        {r.tipo==='receita'?'+':'-'} {fmtBRL(r.valor)}
                      </td>
                      <td style={{padding:'0.5rem 0.6rem'}}>
                        <span style={{padding:'0.15rem 0.5rem',borderRadius:'999px',fontSize:'0.72rem',fontWeight:600,
                                      background:r.tipo==='receita'?'#edf4ee':'#f8ebe9',
                                      color:r.tipo==='receita'?'#2f5236':'#a8483f'}}>
                          {r.tipo==='receita'?'Receita':'Despesa'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        }
      </Card>
    </div>
  );
}

// ── ABA LANÇAMENTOS ───────────────────────────────────────────────────────────
function TabLancamentos({ monthData, onRecordCreated, onRecordDeleted, loading }) {
  const { showError } = useToast();
  const [filter, setFilter] = useState('todos'); // 'todos' | 'receita' | 'despesa'
  const [catFilter, setCatFilter] = useState('');
  const [showModal, setShowModal] = useState(null); // 'receita' | 'despesa' | null
  const [confirm, setConfirm] = useState(null);
  const [deleting, setDeleting] = useState(null);

  const records = monthData?.records||[];
  const allCats = [...new Set(records.map(r=>r.categoria))].sort();
  const filtered = records.filter(r=>
    (filter==='todos'||r.tipo===filter) &&
    (!catFilter||r.categoria===catFilter)
  ).sort((a,b)=>b.data.localeCompare(a.data)||b.id-a.id);

  const handleDelete = async (r) => {
    setConfirm({
      title:'Excluir lançamento',
      message:`Remover "${r.descricao}" (${fmtBRL(r.valor)})? Esta ação não pode ser desfeita.`,
      onConfirm: async () => {
        setConfirm(null);
        setDeleting(r.id);
        try {
          const res = await financialService.delete(r.id);
          if (res.success) onRecordDeleted(r.id);
          else showError(res.message||'Erro ao excluir');
        } catch { showError('Erro ao excluir lançamento'); }
        finally { setDeleting(null); }
      },
    });
  };

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
      {/* Action buttons */}
      <div style={{display:'flex',gap:'0.6rem',flexWrap:'wrap'}}>
        <button onClick={()=>setShowModal('receita')}
          style={{padding:'0.55rem 1.1rem',background: '#ffffff',color: '#2f2a26',
                  border: '1px solid #d9d0c6',borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'0.85rem'}}>
          + Adicionar Receita
        </button>
        <button onClick={()=>setShowModal('despesa')}
          style={{padding:'0.55rem 1.1rem',background:'#f8ebe9',color:'#a8483f',
                  border:'1px solid #c46a60',borderRadius:'8px',cursor:'pointer',fontWeight:600,fontSize:'0.85rem'}}>
          + Adicionar Despesa
        </button>
      </div>

      {/* Filters */}
      <div style={{display:'flex',gap:'0.5rem',alignItems:'center',flexWrap:'wrap'}}>
        {['todos','receita','despesa'].map(f=>(
          <button key={f} onClick={()=>setFilter(f)}
            style={{padding:'0.35rem 0.8rem',borderRadius:'6px',cursor:'pointer',fontWeight:600,fontSize:'0.8rem',
                    border:`1px solid ${filter===f?C.primary:C.border}`,
                    background:filter===f?C.primary+'15':'#ffffff',
                    color:filter===f?C.dark:'#736b64'}}>
            {f==='todos'?'Todos':f==='receita'?'Receitas':'Despesas'}
          </button>
        ))}
        {allCats.length>0 && (
          <select value={catFilter} onChange={e=>setCatFilter(e.target.value)}
            style={{padding:'0.35rem 0.65rem',border:`1px solid ${C.border}`,borderRadius:'6px',
                    fontSize:'0.8rem',color:C.dark,background:'#ffffff',outline:'none'}}>
            <option value="">Todas categorias</option>
            {allCats.map(c=><option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <span style={{color:'#a39b93',fontSize:'0.8rem'}}>{filtered.length} registro{filtered.length!==1?'s':''}</span>
      </div>

      {/* List */}
      <Card>
        {loading
          ? <p style={{textAlign:'center',color:'#a39b93',padding:'2rem'}}>Carregando...</p>
          : filtered.length===0
            ? <p style={{textAlign:'center',color:'#d9d0c6',padding:'1.5rem'}}>Nenhum lançamento encontrado</p>
            : filtered.map(r=>(
              <div key={r.id} style={{display:'flex',alignItems:'center',gap:'0.75rem',
                                      padding:'0.65rem 0',borderBottom:`1px solid ${C.border}`}}>
                <div style={{width:8,height:8,borderRadius:'50%',flexShrink:0,
                             background:r.tipo==='receita'?C.green:C.red}}/>
                <div style={{flex:1,minWidth:0}}>
                  <p style={{margin:0,fontWeight:600,color:C.darker,fontSize:'0.87rem',
                             overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{emo(r.descricao)}</p>
                  <p style={{margin:0,fontSize:'0.73rem',color:'#a39b93'}}>{r.categoria} · {fmtDate(r.data)}</p>
                </div>
                <p style={{margin:0,fontWeight:600,fontSize:'0.95rem',flexShrink:0,
                           color:r.tipo==='receita'?C.green:C.red}}>
                  {r.tipo==='receita'?'+':'-'}{fmtBRL(r.valor)}
                </p>
                {!r.appointment_id && (
                  <button onClick={()=>handleDelete(r)} disabled={deleting===r.id}
                    style={{background:'none',border:'none',cursor:'pointer',color:'#e3b3ad',
                            fontSize:'1rem',padding:'0.2rem',flexShrink:0,opacity:deleting===r.id?.4:1}}>
                    <Ico e="🗑" />
                  </button>
                )}
              </div>
            ))
        }
      </Card>

      {showModal && (
        <NewRecordModal tipo={showModal} onClose={()=>setShowModal(null)} onCreated={onRecordCreated}/>
      )}
      {confirm && (
        <ConfirmModal title={confirm.title} message={confirm.message}
          onConfirm={confirm.onConfirm} onCancel={()=>setConfirm(null)}/>
      )}
    </div>
  );
}

// ── ABA HISTÓRICO ─────────────────────────────────────────────────────────────
function TabHistorico({ history, loading }) {
  if (loading) return <div style={{textAlign:'center',color:'#a39b93',padding:'3rem'}}>Carregando...</div>;
  if (!history.length) return <div style={{textAlign:'center',color:'#a39b93',padding:'3rem'}}>Sem histórico disponível</div>;

  const lineData  = history.map(m=>({name:fmtMonth(m.month),Receita:m.receita}));
  const barData6  = history.slice(-6).map(m=>({name:fmtMonth(m.month),Receita:m.receita,Despesas:m.despesas}));

  // Projection for current month
  const today = new Date();
  const y=today.getFullYear(), mo=today.getMonth();
  const daysInMonth = new Date(y,mo+1,0).getDate();
  const progress = today.getDate()/daysInMonth;
  const last3avg = history.slice(-4,-1).reduce((s,m)=>s+m.receita,0)/3;
  const currMonthRow = history[history.length-1];
  const projection = progress>0&&progress<1 ? currMonthRow?.receita/progress : null;

  return (
    <div style={{display:'flex',flexDirection:'column',gap:'1.25rem'}}>
      {/* Line chart: 12 months */}
      <Card>
        <p style={{margin:'0 0 1rem',fontWeight:600,color:C.darker,fontSize:'0.88rem'}}><Ico e="📈" sp />Evolução da Receita — 12 Meses</p>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={lineData} margin={{top:4,right:4,left:-10,bottom:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f6f0ea"/>
            <XAxis dataKey="name" tick={{fontSize:11,fill:'#a39b93'}}/>
            <YAxis tick={{fontSize:11,fill:'#a39b93'}} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
            <Tooltip content={<FinTooltip/>}/>
            <Line type="monotone" dataKey="Receita" stroke={C.primary} strokeWidth={2.5}
              dot={{fill:C.primary,r:4,strokeWidth:0}} activeDot={{r:6}}/>
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Bar chart: 6 months comparative */}
      <Card>
        <p style={{margin:'0 0 1rem',fontWeight:600,color:C.darker,fontSize:'0.88rem'}}><Ico e="📊" sp />Comparativo Últimos 6 Meses</p>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={barData6} margin={{top:4,right:4,left:-10,bottom:0}}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f6f0ea"/>
            <XAxis dataKey="name" tick={{fontSize:11,fill:'#a39b93'}}/>
            <YAxis tick={{fontSize:11,fill:'#a39b93'}} tickFormatter={v=>v>=1000?`${(v/1000).toFixed(0)}k`:v}/>
            <Tooltip content={<FinTooltip/>}/>
            <Legend iconSize={9} wrapperStyle={{fontSize:'0.75rem'}}/>
            <Bar dataKey="Receita"  fill={C.green}   radius={[3,3,0,0]}/>
            <Bar dataKey="Despesas" fill={C.red}     radius={[3,3,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Projection */}
      {projection && (
        <Card style={{background:'linear-gradient(135deg,#fbf8f5,#ffffff)',border:`1px solid ${C.primary}40`}}>
          <p style={{margin:'0 0 0.75rem',fontWeight:600,color:C.darker,fontSize:'0.88rem'}}><Ico e="🔮" sp />Projeção do Mês Atual</p>
          <div style={{display:'flex',gap:'1.5rem',flexWrap:'wrap'}}>
            <div>
              <p style={{margin:'0 0 0.1rem',fontSize:'0.72rem',color:'#736b64',textTransform:'uppercase',fontWeight:600}}>Receita já registrada</p>
              <p style={{margin:0,fontSize:'1.25rem',fontWeight:700,color:C.green}}>{fmtBRL(currMonthRow?.receita)}</p>
            </div>
            <div>
              <p style={{margin:'0 0 0.1rem',fontSize:'0.72rem',color:'#736b64',textTransform:'uppercase',fontWeight:600}}>Projeção até o fim do mês</p>
              <p style={{margin:0,fontSize:'1.25rem',fontWeight:700,color:C.primary}}>{fmtBRL(projection)}</p>
            </div>
            <div>
              <p style={{margin:'0 0 0.1rem',fontSize:'0.72rem',color:'#736b64',textTransform:'uppercase',fontWeight:600}}>Média dos últimos 3 meses</p>
              <p style={{margin:0,fontSize:'1.25rem',fontWeight:700,color:C.blue}}>{fmtBRL(last3avg)}</p>
            </div>
            <div>
              <p style={{margin:'0 0 0.1rem',fontSize:'0.72rem',color:'#736b64',textTransform:'uppercase',fontWeight:600}}>Progresso do mês</p>
              <div style={{marginTop:'0.3rem'}}>
                <div style={{height:6,background:'#f6f0ea',borderRadius:'999px',overflow:'hidden',width:120}}>
                  <div style={{height:'100%',width:`${Math.round(progress*100)}%`,background:C.primary,borderRadius:'999px'}}/>
                </div>
                <p style={{margin:'0.2rem 0 0',fontSize:'0.72rem',color:'#a39b93'}}>{Math.round(progress*100)}% concluído</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Monthly summary table */}
      <Card>
        <p style={{margin:'0 0 1rem',fontWeight:600,color:C.darker,fontSize:'0.88rem'}}><Ico e="📋" sp />Resumo Mensal</p>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.85rem'}}>
            <thead>
              <tr style={{borderBottom:`2px solid ${C.border}`}}>
                {['Mês','Receitas','Despesas','Lucro','Variação'].map(h=>(
                  <th key={h} style={{textAlign:'left',padding:'0.4rem 0.6rem',fontSize:'0.72rem',
                                       color:'#a39b93',textTransform:'uppercase',fontWeight:600}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...history].reverse().map((m,i)=>{
                const prev = history[history.length-2-i];
                const varPct = prev&&prev.receita>0 ? ((m.receita-prev.receita)/prev.receita)*100 : null;
                return (
                  <tr key={m.month} style={{borderBottom:`1px solid ${C.border}`}}>
                    <td style={{padding:'0.5rem 0.6rem',fontWeight:600,color:C.darker,whiteSpace:'nowrap'}}>
                      {MONTHS_PT[parseInt(m.month.split('-')[1],10)-1].slice(0,3)} {emo(m.month.split('-')[0])}
                    </td>
                    <td style={{padding:'0.5rem 0.6rem',color:C.green,fontWeight:600}}>{fmtBRL(m.receita)}</td>
                    <td style={{padding:'0.5rem 0.6rem',color:C.red,fontWeight:600}}>{fmtBRL(m.despesas)}</td>
                    <td style={{padding:'0.5rem 0.6rem',fontWeight:600,color:m.lucro>=0?C.green:C.red}}>{fmtBRL(m.lucro)}</td>
                    <td style={{padding:'0.5rem 0.6rem'}}>
                      {varPct!==null && (
                        <span style={{fontSize:'0.78rem',fontWeight:600,
                                      color:varPct>=0?C.green:C.red}}>
                          {varPct>=0?'▲':'▼'} {Math.abs(varPct).toFixed(1)}%
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ── MAIN FINANCEIRO ───────────────────────────────────────────────────────────
export default function Financeiro() {
  const { showError } = useToast();

  const now = new Date();
  const [tab,       setTab]       = useState('resumo');
  const [selYear,   setSelYear]   = useState(now.getFullYear());
  const [selMonth,  setSelMonth]  = useState(now.getMonth()+1);
  const [monthData, setMonthData] = useState(null);
  const [history,   setHistory]   = useState([]);
  const [settings,  setSettings]  = useState(null);
  const [loadingMonth,   setLoadingMonth]   = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(true);

  const monthStr = `${selYear}-${String(selMonth).padStart(2,'0')}`;

  // Load settings once
  useEffect(()=>{
    settingsService.get().then(r=>{ if(r.success) setSettings(r.data); }).catch(()=>{});
  },[]);

  // Load month data
  const loadMonth = useCallback(async()=>{
    setLoadingMonth(true);
    try {
      const res = await financialService.getByMonth(monthStr);
      if (res.success) setMonthData(res.data);
    } catch { showError('Erro ao carregar dados do mês'); }
    finally { setLoadingMonth(false); }
  },[monthStr]); // eslint-disable-line

  // Load history
  const loadHistory = useCallback(async()=>{
    setLoadingHistory(true);
    try {
      const res = await financialService.getMonthsChart(12);
      if (res.success) setHistory(res.data||[]);
    } catch {}
    finally { setLoadingHistory(false); }
  },[]);

  useEffect(()=>{ loadMonth(); },[loadMonth]);
  useEffect(()=>{ loadHistory(); },[loadHistory]);

  // Mutation handlers
  const onRecordCreated = (rec) => {
    // Reload month data
    loadMonth();
  };

  const onRecordDeleted = (id) => {
    setMonthData(prev => prev ? {
      ...prev,
      records: prev.records.filter(r=>r.id!==id),
      receita:  prev.receita  - (prev.records.find(r=>r.id===id&&r.tipo==='receita')?.valor||0),
      despesas: prev.despesas - (prev.records.find(r=>r.id===id&&r.tipo==='despesa')?.valor||0),
    } : null);
    loadMonth(); // also reload for accuracy
  };

  const TABS = [
    { key:'resumo',       label:'📈 Resumo do Mês' },
    { key:'lancamentos',  label:'📝 Lançamentos' },
    { key:'historico',    label:'📊 Histórico' },
  ];

  return (
    <div style={{maxWidth:'1200px',margin:'0 auto',padding:'0 0.25rem',display:'flex',flexDirection:'column',gap:'1.25rem'}}>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:'0.75rem'}}>
        <div>
          <h1 style={{margin:'0 0 0.2rem',fontSize:'1.65rem',fontWeight:700,color:C.darker}}>Financeiro</h1>
          <p style={{margin:0,color:'#a39b93',fontSize:'0.87rem'}}>Controle financeiro do consultório</p>
        </div>
        {/* Month selector */}
        <div style={{display:'flex',gap:'0.5rem',alignItems:'center'}}>
          <select value={selMonth} onChange={e=>setSelMonth(Number(e.target.value))}
            style={{padding:'0.45rem 0.65rem',border:`1px solid ${C.border}`,borderRadius:'8px',
                    fontSize:'0.88rem',color:C.darker,background:'#ffffff',outline:'none'}}>
            {MONTHS_PT.map((m,i)=><option key={i+1} value={i+1}>{m}</option>)}
          </select>
          <select value={selYear} onChange={e=>setSelYear(Number(e.target.value))}
            style={{padding:'0.45rem 0.65rem',border:`1px solid ${C.border}`,borderRadius:'8px',
                    fontSize:'0.88rem',color:C.darker,background:'#ffffff',outline:'none'}}>
            {[now.getFullYear()-2,now.getFullYear()-1,now.getFullYear(),now.getFullYear()+1].map(y=>(
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',background:C.bg,borderRadius:'10px',padding:'3px',
                   border:`1px solid ${C.border}`,gap:'2px',width:'fit-content'}}>
        {TABS.map(t=>(
          <button key={t.key} onClick={()=>setTab(t.key)}
            style={{padding:'0.45rem 1rem',borderRadius:'7px',cursor:'pointer',fontWeight:600,
                    fontSize:'0.83rem',border:'none',
                    background:tab===t.key?C.primary:'transparent',
                    color:tab===t.key?'#ffffff':C.dark}}>
            {emo(t.label)}
          </button>
        ))}
      </div>

      {/* Content */}
      {tab==='resumo'      && <TabResumo  monthData={monthData} settings={settings} loading={loadingMonth}/>}
      {tab==='lancamentos' && <TabLancamentos monthData={monthData} loading={loadingMonth}
                                onRecordCreated={onRecordCreated} onRecordDeleted={onRecordDeleted}/>}
      {tab==='historico'   && <TabHistorico history={history} loading={loadingHistory}/>}
    </div>
  );
}
